﻿import { htmlEncode, resolveUrl, localText, format } from "@serenity-is/corelib/q";

export interface IdleTimeoutOptions {
    /** which events to consider as an activity. */
    activityEvents?: string;
    /** signout user if no activity within this duration (seconds). default is 900 (15 mins). */
    activityTimeout?: number;
    /** optional callback that is called just before signing out */
    beforeSignout?: () => void;
    /** interval for checking timer, default is 5 seconds */
    checkInterval?: number;
    /** interval for calling keep alive url in seconds, default is 300 (5 mins) */
    keepAliveTimer?: number;
    /** keep alive url */
    keepAliveUrl?: string;
    /** url used to signout user */
    signoutUrl?: string;
    /** show warning for this number of seconds */
    warningDuration?: number;
}

export class IdleTimeout {

    private options: IdleTimeoutOptions;
    private idleTimer: number;
    private keepAliveTimer: number;
    private warningTimer: number;
    private remainingTimer: number;

    constructor(options: IdleTimeoutOptions) {
        this.options = $.extend({}, IdleTimeout.defaults, options);

        localStorage.setItem('IdleTimeout:LastActivity', $.now().toString());
        localStorage.removeItem('IdleTimeout:Signout');

        var mouseX: number, mouseY: number;
        $('body').on(this.options.activityEvents, e => {

            if (e.type == 'mousemove') {

                if (e.clientX == mouseX && e.clientY == mouseY)
                    return;

                mouseX = e.clientX;
                mouseY = e.clientY;
            }

            if (!this.isWarningActive()) {
                this.resetIdleTimer();
            }
        });

        if (this.options.keepAliveTimer)
            this.startKeepAliveTimer();

        this.resetIdleTimer();
    }

    protected startKeepAliveTimer() {

        clearTimeout(this.keepAliveTimer);

        var keepSessionAlive = () => {
            $.get(resolveUrl(this.options.keepAliveUrl) + '?' + new Date().getTime());
            this.startKeepAliveTimer();
        };

        this.keepAliveTimer = setTimeout(keepSessionAlive, this.options.keepAliveTimer * 1000);
    }

    protected isWarningActive() {
        return $('#idle-timeout-warning').is(':visible');
    }

    protected startWarningTimer() {
        this.warningTimer = setInterval(() => this.warningTimeout(), this.options.checkInterval * 1000);
    }

    protected stopWarningTimer() {
        clearInterval(this.warningTimer);
        clearInterval(this.remainingTimer);
    };

    protected warningTimeout() {
        if (this.getRemainingSeconds() < 0)
            this.signout();
    }

    protected resetIdleTimer() {
        clearTimeout(this.idleTimer);
        localStorage.setItem('IdleTimeout:LastActivity', $.now().toString());
        this.idleTimeout();
    };

    protected idleTimeout() {
        this.checkActivityTimeout();
        this.idleTimer = setTimeout(() => this.idleTimeout(), this.options.checkInterval * 1000);
    };

    protected checkActivityTimeout() {

        var timeLimit = parseInt(localStorage.getItem('IdleTimeout:LastActivity') || $.now().toString()) +
            (this.options.activityTimeout - (this.options.warningDuration || 0)) * 1000;

        if ($.now() > timeLimit) {
            if (!this.options.warningDuration) {
                this.signout();
            } else {
                this.showWarning();
                this.startWarningTimer();
            }
        } else if (localStorage.getItem('IdleTimeout:Signout')) {
            this.signout();
        } else if (this.isWarningActive()) {
            this.closeWarning();
            this.stopWarningTimer();
        }
    }

    protected signout() {
        localStorage.setItem('IdleTimeout:Signout', "1");
        clearTimeout(this.keepAliveTimer);
        this.stopWarningTimer();
        this.closeWarning();
        this.options.beforeSignout && this.options.beforeSignout();
        if (this.options.signoutUrl)
            window.location.href = resolveUrl(this.options.signoutUrl);
    }

    protected showWarning() {
        var warningDialog = $('#idle-timeout-warning');
        if (!warningDialog.length) {
            warningDialog = $(`<div class="modal fade" id="idle-timeout-warning">
<div class="modal-dialog">
    <div class="modal-content">
        <div class="modal-header">
            <h4 class="modal-title">${htmlEncode(localText("Controls.IdleTimeout.WarningTitle"))} </h4>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-hidden="true"></button>
        </div>
        <div class="modal-body">
        <p>${htmlEncode(localText("Controls.IdleTimeout.WarningMessage"))}</p>
        <p>${format(htmlEncode(localText("Controls.IdleTimeout.CountdownMessage")), `<span id='idle-timeout-seconds'></span>&nbsp;`)}</p>
        <div class="progress">
            <div id="idle-timeout-progress" class="progress-bar progress-bar-striped active" role="progressbar" 
                style="min-width: 15px; width: 100%;">
            </div>
        </div>
        <div class="modal-footer">
            <button id="idle-timeout-warning-signout" type="button" class="btn btn-danger">
                ${htmlEncode(localText("Controls.IdleTimeout.SignoutButton"))}
            </button>
            <button id="idle-timeout-warning-keepalive" type="button" class="btn btn-primary" data-bs-dismiss="modal">
                ${htmlEncode(localText("Controls.IdleTimeout.KeepAliveButton"))}
            </button>
        </div>
    </div>
</div>
</div>`).appendTo(document.body);

            $('#idle-timeout-warning-signout').on('click', () => this.signout());
            warningDialog.on('hide.bs.modal', () => {
                clearTimeout(this.warningTimer);
                clearTimeout(this.remainingTimer);
                this.resetIdleTimer();
            });
        }

        warningDialog['modal']('show');
        this.countdownDisplay();
    }

    protected closeWarning() {
        var warning = $('#idle-timeout-warning').off('hide.bs.modal');
        if (warning.length && warning.data() && warning.data()['bs.modal'] && warning.data()['bs.modal'].isShown) {
            warning.removeClass('fade');
            warning['modal']('hide');
            warning.remove();
        }
    }

    protected getRemainingSeconds() {
        var timeLimit = parseInt(localStorage.getItem('IdleTimeout:LastActivity') || $.now().toString()) +
            (this.options.activityTimeout * 1000);
        return Math.ceil(((timeLimit - $.now()) / 1000));
    }

    protected countdownDisplay() {
        clearTimeout(this.remainingTimer);
        var update = () => {
            var remainingSeconds = Math.max(this.getRemainingSeconds(), 1);
            var percentLeft = 100 * Math.max(1, remainingSeconds) / (this.options.warningDuration || 1);
            $('#idle-timeout-progress').css('width', percentLeft + '%');
            $('#idle-timeout-seconds').text(remainingSeconds);
        };
        this.remainingTimer = setInterval(update, 500);
        update();
    };

    static defaults: IdleTimeoutOptions = {
        activityEvents: 'click keyup mouseup mousemove scroll touchend touchmove wheel mousewheel',
        activityTimeout: 900,
        checkInterval: 5,
        keepAliveTimer: 300,
        keepAliveUrl: '~/Account/KeepAlive',
        signoutUrl: '~/Account/Signout',
        warningDuration: 60,
    }
}
